import type { Item, ItemDetails, Place, Trip } from '../schema';
import { instantFromLocal } from '../time';

/**
 * Test fixture modeled on Matthew's current trip: Delta RSW to MSP on an evening departure
 * (EDT to CDT), a Hertz rental from MSP for two weeks, and Crowne Plaza, then an Airbnb,
 * then the same Crowne Plaza. It must read as one 17-day Minneapolis trip with three
 * back-to-back stays. Sample data only.
 */

const ET = 'America/New_York';
const CT = 'America/Chicago';
const T0 = Date.UTC(2026, 8, 1);

function base(id: string) {
  return { id, rev: 0, updatedAt: T0, createdAt: T0, deleted: false };
}

function place(id: string, p: Partial<Place> & Pick<Place, 'name' | 'kind'>): Place {
  return {
    ...base(id),
    address: null,
    lat: null,
    lon: null,
    tz: null,
    city: null,
    region: null,
    regionCode: null,
    country: 'United States',
    countryCode: 'US',
    iata: null,
    geo: 'dataset',
    ...p,
  };
}

export const MPLS_PLACES: Place[] = [
  place('plc_rsw', {
    name: 'Southwest Florida International Airport',
    kind: 'airport',
    iata: 'RSW',
    lat: 26.536167,
    lon: -81.755167,
    tz: ET,
    city: 'Fort Myers',
    region: 'Florida',
    regionCode: 'FL',
  }),
  place('plc_msp', {
    name: 'Minneapolis-Saint Paul International Airport',
    kind: 'airport',
    iata: 'MSP',
    lat: 44.881956,
    lon: -93.221767,
    tz: CT,
    city: 'Minneapolis',
    region: 'Minnesota',
    regionCode: 'MN',
  }),
  place('plc_cp', {
    name: 'Crowne Plaza AiRE MSP Airport - Mall of America',
    kind: 'lodging',
    address: '3 Appletree Square, Bloomington, MN 55425',
    lat: 44.8597,
    lon: -93.2263,
    tz: CT,
    city: 'Bloomington',
    region: 'Minnesota',
    regionCode: 'MN',
    geo: 'geocoder',
  }),
  place('plc_bnb', {
    name: 'Airbnb in Linden Hills',
    kind: 'lodging',
    address: 'Linden Hills, Minneapolis, MN 55410',
    lat: 44.9244,
    lon: -93.3155,
    tz: CT,
    city: 'Minneapolis',
    region: 'Minnesota',
    regionCode: 'MN',
    geo: 'geocoder',
  }),
];

function item(
  id: string,
  p: Omit<Partial<Item>, 'details'> & Pick<Item, 'type' | 'title'> & { details: ItemDetails },
): Item {
  return {
    ...base(id),
    tripId: 'trp_mpls',
    tripLocked: false,
    status: 'active',
    startAt: null,
    startTz: null,
    endAt: null,
    endTz: null,
    allDay: false,
    startPlaceId: null,
    endPlaceId: null,
    provider: null,
    confirmation: null,
    phone: null,
    url: null,
    notes: '',
    sourceId: null,
    cost: null,
    ...p,
  };
}

export const MPLS_ITEMS: Item[] = [
  item('itm_dl_out', {
    type: 'flight',
    title: 'Delta 2187',
    provider: 'Delta Air Lines',
    confirmation: 'GXR7KP',
    startAt: instantFromLocal('2026-09-30T19:05', ET),
    startTz: ET,
    endAt: instantFromLocal('2026-09-30T21:32', CT),
    endTz: CT,
    startPlaceId: 'plc_rsw',
    endPlaceId: 'plc_msp',
    details: {
      type: 'flight',
      airline: 'DL',
      flightNumber: '2187',
      origin: 'RSW',
      destination: 'MSP',
      seat: '14A',
      cabin: 'Main',
    },
  }),
  item('itm_hertz', {
    type: 'car',
    title: 'Hertz',
    provider: 'Hertz',
    confirmation: 'H4410982',
    startAt: instantFromLocal('2026-09-30T22:05', CT),
    startTz: CT,
    endAt: instantFromLocal('2026-10-14T09:00', CT),
    endTz: CT,
    startPlaceId: 'plc_msp',
    endPlaceId: 'plc_msp',
    details: { type: 'car', company: 'Hertz', carClass: 'Midsize SUV' },
  }),
  item('itm_cp1', {
    type: 'lodging',
    title: 'Crowne Plaza AiRE MSP Airport',
    provider: 'Crowne Plaza',
    confirmation: '61834927',
    startAt: instantFromLocal('2026-09-30T22:40', CT),
    startTz: CT,
    endAt: instantFromLocal('2026-10-03T11:00', CT),
    endTz: CT,
    startPlaceId: 'plc_cp',
    details: {
      type: 'lodging',
      kind: 'hotel',
      propertyName: 'Crowne Plaza AiRE',
      brand: 'Crowne Plaza',
      program: 'IHG One Rewards',
    },
  }),
  item('itm_bnb', {
    type: 'lodging',
    title: 'Airbnb in Linden Hills',
    provider: 'Airbnb',
    confirmation: 'HMQ4ZK9P2T',
    startAt: instantFromLocal('2026-10-03T16:00', CT),
    startTz: CT,
    endAt: instantFromLocal('2026-10-13T10:00', CT),
    endTz: CT,
    startPlaceId: 'plc_bnb',
    details: {
      type: 'lodging',
      kind: 'airbnb',
      propertyName: 'Airbnb in Linden Hills',
      host: 'Dana',
    },
  }),
  item('itm_cp2', {
    type: 'lodging',
    title: 'Crowne Plaza AiRE MSP Airport',
    provider: 'Crowne Plaza',
    confirmation: '61839455',
    startAt: instantFromLocal('2026-10-13T15:00', CT),
    startTz: CT,
    endAt: instantFromLocal('2026-10-16T11:00', CT),
    endTz: CT,
    startPlaceId: 'plc_cp',
    details: {
      type: 'lodging',
      kind: 'hotel',
      propertyName: 'Crowne Plaza AiRE',
      brand: 'Crowne Plaza',
      program: 'IHG One Rewards',
    },
  }),
  item('itm_dl_back', {
    type: 'flight',
    title: 'Delta 1124',
    provider: 'Delta Air Lines',
    confirmation: 'GXR7KP',
    startAt: instantFromLocal('2026-10-16T17:10', CT),
    startTz: CT,
    endAt: instantFromLocal('2026-10-16T21:15', ET),
    endTz: ET,
    startPlaceId: 'plc_msp',
    endPlaceId: 'plc_rsw',
    details: {
      type: 'flight',
      airline: 'DL',
      flightNumber: '1124',
      origin: 'MSP',
      destination: 'RSW',
      seat: '9C',
      cabin: 'Main',
    },
  }),
];

export const MPLS_TRIP: Trip = {
  ...base('trp_mpls'),
  title: null,
  startDate: null,
  endDate: null,
  coverAttachmentId: null,
  notes: '',
  pinned: false,
};

export function placeLookup(places: Place[]) {
  const m = new Map(places.map((p) => [p.id, p]));
  return (id: string | null | undefined) => (id ? m.get(id) : undefined);
}
